(() => {
  'use strict';

  if (window.__showgiftSearchPrecisionV7) return;
  window.__showgiftSearchPrecisionV7 = true;

  const PRECISE_FIELDS = 'title,variants.sku';
  const DISCOVERY_FIELDS = 'title,tag,vendor,product_type,variants.sku';
  const ORIGINAL_PARAM = 'sgp_q';
  const ORIGINAL_STORAGE_KEY = 'showgift_search_original_query';
  const STOP_WORDS = new Set([
    'de','du','des','le','la','les','un','une','et','pour','avec','sans','sur','en','a','au','aux',
    'the','of','for','with','and','to','in','on'
  ]);
  const GENERIC_PREFIXES = [
    'ddr','gddr','pcie','pci','wifi','usb','hdmi','displayport','atx','sata','gen','lga','socket','nvme','80plus','cl','12v'
  ];
  const GENERIC_SUFFIXES = [
    'ghz','mhz','khz','hz','gb','tb','go','to','mb','mo','kb','ko','mm','cm','kw','w','bit','bits','cores','threads'
  ];
  const MODEL_FAMILIES = ['rtx','gtx','rx','ryzen','radeon','geforce','arc','xeon','threadripper','samsung','intel','amd'];
  const CAPACITY_UNIT_MAP = { to: 'tb', tb: 'tb', go: 'gb', gb: 'gb', mo: 'mb', mb: 'mb', ko: 'kb', kb: 'kb' };
  const CAPACITY_RE = /(\d+(?:[.,]\d+)?)\s*(to|tb|go|gb|mo|mb|ko|kb)\b/gi;

  const normalize = (value = '') => String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_\-\/]+/g, ' ')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const escapeRegex = value => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  function parseCapacities(value = '') {
    const found = [];
    const source = String(value).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    let match;
    CAPACITY_RE.lastIndex = 0;
    while ((match = CAPACITY_RE.exec(source)) !== null) {
      const number = String(Number(match[1].replace(',', '.')));
      const unit = CAPACITY_UNIT_MAP[match[2]] || match[2];
      found.push(`${number}:${unit}`);
    }
    return [...new Set(found)];
  }

  function stripCapacities(value = '') {
    CAPACITY_RE.lastIndex = 0;
    return String(value).replace(CAPACITY_RE, ' ');
  }

  function meaningfulTokens(query = '') {
    return normalize(stripCapacities(query))
      .split(' ')
      .filter(token => token && token.length > 1 && !STOP_WORDS.has(token));
  }

  function strongReferenceToken(query = '') {
    const tokens = normalize(query).split(' ').filter(Boolean);
    const hasModelFamily = tokens.some(token => MODEL_FAMILIES.includes(token));
    let best = '';

    for (let index = 0; index < tokens.length; index += 1) {
      const token = tokens[index];
      const previous = tokens[index - 1] || '';
      const next = tokens[index + 1] || '';

      if (token.length >= 4 && /[a-z]/.test(token) && /\d/.test(token)) {
        if (GENERIC_PREFIXES.some(prefix => token.startsWith(prefix))) continue;
        if (GENERIC_SUFFIXES.some(suffix => token.endsWith(suffix))) continue;
        if (token.length > best.length) best = token;
        continue;
      }

      if (/^\d{4,5}$/.test(token) && hasModelFamily) {
        if (CAPACITY_UNIT_MAP[previous] || CAPACITY_UNIT_MAP[next]) continue;
        if (token.length > best.length) best = token;
        continue;
      }

      if (/^\d{3}$/.test(token) && tokens.includes('samsung')) {
        if (['pro','evo','qvo'].includes(previous) || ['pro','evo','qvo'].includes(next)) {
          if (token.length > best.length) best = token;
        }
      }
    }

    return best;
  }

  function isPlatformDiscoveryQuery(query = '') {
    const value = normalize(query);
    return value === 'intel' || value === 'amd';
  }

  function shouldStrictFilter(query = '') {
    if (isPlatformDiscoveryQuery(query)) return false;
    const tokens = meaningfulTokens(query);
    const capacities = parseCapacities(query);
    const hasModelFamily = tokens.some(token => MODEL_FAMILIES.includes(token));
    return hasModelFamily || Boolean(strongReferenceToken(query)) || (capacities.length > 0 && tokens.length > 0) || tokens.length >= 2;
  }

  function platformStorefrontQuery(query = '') {
    const platform = normalize(query);
    if (platform === 'intel') {
      return '(title:"Intel" OR vendor:"Intel" OR product_type:"Intel" OR tag:"Intel" OR tag:"LGA1700" OR tag:"LGA1851" OR tag:"LGA1200" OR tag:"B860" OR tag:"Z890" OR tag:"H810" OR tag:"B760" OR tag:"Z790" OR tag:"H770" OR tag:"H610" OR tag:"B660" OR tag:"Z690" OR tag:"H670" OR tag:"B560" OR tag:"Z590" OR tag:"B460" OR tag:"Z490")';
    }
    if (platform === 'amd') {
      return '(title:"AMD" OR title:"Ryzen" OR title:"Radeon" OR vendor:"AMD" OR product_type:"AMD" OR tag:"AMD" OR tag:"AM4" OR tag:"AM5" OR tag:"B850" OR tag:"X870" OR tag:"X870E" OR tag:"B650" OR tag:"B650E" OR tag:"X670" OR tag:"X670E" OR tag:"A620" OR tag:"B550" OR tag:"X570" OR tag:"B450" OR tag:"X470" OR tag:"A520")';
    }
    return displayQuery(query);
  }

  function shouldServerStrictFilter(query = '') {
    const tokens = meaningfulTokens(query);
    return tokens.some(token => MODEL_FAMILIES.includes(token));
  }

  function strictStorefrontQuery(query = '') {
    const original = displayQuery(query);
    if (isPlatformDiscoveryQuery(original)) return platformStorefrontQuery(original);
    if (!shouldServerStrictFilter(original)) return original;

    const tokens = meaningfulTokens(original);
    const clauses = [];
    const seen = new Set();

    for (const token of tokens) {
      if (seen.has(token)) continue;
      seen.add(token);
      clauses.push(`title:"${token.replace(/"/g, '\\"')}"`);
    }

    return clauses.length ? clauses.join(' AND ') : original;
  }

  function displayQuery(query = '') {
    return String(query || '').replace(/\"([a-z0-9]+)\"/gi, '$1').trim();
  }

  function looksStructuredQuery(query = '') {
    return /(?:^|\s|\()title\s*:/i.test(String(query)) || /\s(?:AND|OR|NOT)\s/i.test(String(query));
  }

  function storeOriginalQuery(query = '') {
    const value = String(query || '').trim();
    if (!value) return;
    try { window.sessionStorage.setItem(ORIGINAL_STORAGE_KEY, value); } catch (_) {}
  }

  function storedOriginalQuery() {
    try { return window.sessionStorage.getItem(ORIGINAL_STORAGE_KEY) || ''; } catch (_) { return ''; }
  }

  function originalQueryFromUrl(url = window.location.href) {
    try {
      const parsed = new URL(String(url), window.location.origin);
      const explicit = (parsed.searchParams.get(ORIGINAL_PARAM) || '').trim();
      if (explicit) return explicit;
      const raw = (parsed.searchParams.get('q') || '').trim();
      if (looksStructuredQuery(raw)) return storedOriginalQuery().trim();
      return displayQuery(raw);
    } catch (_) {
      return '';
    }
  }

  function platformMatchesMeta(meta = '', query = '') {
    const platform = normalize(query);
    const normalizedMeta = normalize(meta);
    const compactMeta = normalizedMeta.replace(/\s+/g, '');
    const tokens = new Set(normalizedMeta.split(' ').filter(Boolean));

    if (platform === 'intel') {
      const intelChipsets = new Set(['b860','z890','h810','b760','z790','h770','h610','b660','z690','h670','b560','z590','b460','z490']);
      return tokens.has('intel')
        || [...intelChipsets].some(value => tokens.has(value))
        || compactMeta.includes('lga1700')
        || compactMeta.includes('lga1851')
        || compactMeta.includes('lga1200');
    }

    if (platform === 'amd') {
      const amdChipsets = new Set(['b850','x870','x870e','b650','b650e','x670','x670e','a620','b550','x570','b450','x470','a520']);
      return tokens.has('amd')
        || tokens.has('ryzen')
        || tokens.has('radeon')
        || tokens.has('am4')
        || tokens.has('am5')
        || [...amdChipsets].some(value => tokens.has(value));
    }

    return true;
  }

  function titleMatchesQuery(title = '', query = '') {
    if (!shouldStrictFilter(query)) return true;

    const titleNormalized = normalize(title);
    const titleTokens = new Set(titleNormalized.split(' ').filter(Boolean));
    const requiredTokens = meaningfulTokens(query);
    const requiredCapacities = parseCapacities(query);
    const titleCapacities = new Set(parseCapacities(title));

    for (const token of requiredTokens) {
      if (!titleTokens.has(token)) return false;
    }
    for (const capacity of requiredCapacities) {
      if (!titleCapacities.has(capacity)) return false;
    }
    return true;
  }

  let lastQuery = '';
  let filterQueued = false;

  function getCurrentQuery() {
    if (lastQuery) return displayQuery(lastQuery);

    const fromUrl = originalQueryFromUrl();
    if (fromUrl) return fromUrl;

    const active = document.activeElement;
    if (active instanceof HTMLInputElement && active.type === 'search' && active.value.trim()) {
      return displayQuery(active.value);
    }

    const visibleInput = [...document.querySelectorAll('input[type="search"]')].find(input => {
      if (!(input instanceof HTMLInputElement) || !input.value.trim()) return false;
      const style = window.getComputedStyle(input);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
    if (visibleInput) return displayQuery(visibleInput.value);

    return storedOriginalQuery().trim();
  }

  function restoreHiddenCards() {
    document.querySelectorAll('[data-sgp-search-hidden="true"]').forEach(element => {
      element.hidden = false;
      element.style.removeProperty('display');
      element.removeAttribute('data-sgp-search-hidden');
    });
  }

  function cardHolder(card) {
    if (card.closest('#product-grid')) return card.closest('.grid__item') || card;
    return card;
  }

  function filterRenderedCards() {
    cleanPredictiveSearchLabels();
    filterQueued = false;
    const query = getCurrentQuery();
    restoreHiddenCards();
    if (!query) return;

    const cards = document.querySelectorAll(
      '#predictive-search .product-item, .predictive-search--header .product-item, #product-grid .product-item'
    );

    if (isPlatformDiscoveryQuery(query)) {
      cards.forEach(card => {
        const meta = card.getAttribute('data-sgp-search-meta')
          || card.closest('.grid__item')?.getAttribute('data-sgp-platform-meta')
          || '';
        if (!platformMatchesMeta(meta, query)) {
          const holder = cardHolder(card);
          holder.hidden = true;
          holder.style.setProperty('display', 'none', 'important');
          holder.setAttribute('data-sgp-search-hidden', 'true');
        }
      });
      return;
    }

    if (!shouldStrictFilter(query)) return;

    cards.forEach(card => {
      const titleNode = card.querySelector('.product-item__name[aria-label]') || card.querySelector('.product-item__name');
      const title = titleNode?.getAttribute('aria-label') || titleNode?.textContent || '';
      if (!titleMatchesQuery(title, query)) {
        const holder = cardHolder(card);
        holder.hidden = true;
        holder.style.setProperty('display', 'none', 'important');
        holder.setAttribute('data-sgp-search-hidden', 'true');
      }
    });
  }


  function cleanPredictiveSearchLabels() {
    const original = getCurrentQuery() || storedOriginalQuery().trim();
    if (!original) return;

    document.querySelectorAll('#predictive-search .search-list-item, .predictive-search--header .search-list-item').forEach(container => {
      const heading = container.querySelector('.fs-18.subheading_weight');
      if (heading && /(?:title|tag|vendor|product_type)\s*:/i.test(heading.textContent || '')) {
        heading.textContent = 'Rechercher « ' + original + ' »';
      }

      container.querySelectorAll('a[href*="/search"]').forEach(anchor => {
        if (anchor instanceof HTMLAnchorElement) refineSearchLink(anchor);
      });
    });
  }

  function scheduleFilter() {
    if (filterQueued) return;
    filterQueued = true;
    window.requestAnimationFrame(() => window.setTimeout(filterRenderedCards, 20));
  }

  function strictPredictiveQuery(query = '') {
    const original = displayQuery(query);
    const token = strongReferenceToken(original);
    if (!token) return original;

    const quoted = new RegExp(`\"${escapeRegex(token)}\"`, 'i');
    if (quoted.test(original)) return original;

    const boundary = new RegExp(`(^|[^a-z0-9])(${escapeRegex(token)})(?=$|[^a-z0-9])`, 'i');
    if (!boundary.test(original)) return original;
    return original.replace(boundary, '$1\"$2\"');
  }

  function refinePredictiveUrl(rawUrl) {
    try {
      const url = new URL(String(rawUrl), window.location.origin);
      if (!url.pathname.includes('/search/suggest')) return rawUrl;

      const query = displayQuery(url.searchParams.get('q') || '');
      const discovery = isPlatformDiscoveryQuery(query);
      if (!discovery && !shouldStrictFilter(query)) return rawUrl;

      lastQuery = query;
      storeOriginalQuery(query);

      // Predictive search must always keep the human query visible.
      // Platform searches (intel/amd) are broadened with fields, then cleaned client-side.
      if (!discovery && strongReferenceToken(query)) {
        url.searchParams.set('q', strictPredictiveQuery(query));
      } else {
        url.searchParams.set('q', query);
      }
      url.searchParams.set('resources[options][fields]', discovery ? DISCOVERY_FIELDS : PRECISE_FIELDS);
      url.searchParams.set('resources[options][prefix]', 'none');
      return url.toString();
    } catch (_) {
      return rawUrl;
    }
  }

  function upsertHiddenInput(form, name, value) {
    const matches = [...form.querySelectorAll(`input[name="${name}"]`)];
    let input = matches.find(node => node.type === 'hidden') || matches[0];
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
    matches.forEach(node => {
      if (node !== input && node.type === 'hidden') node.remove();
    });
    return input;
  }

  function removeHiddenInputs(form, name) {
    form.querySelectorAll(`input[type="hidden"][name="${name}"]`).forEach(input => input.remove());
  }

  function isSearchForm(form) {
    try {
      const action = new URL(form.getAttribute('action') || window.location.href, window.location.origin);
      return action.pathname.endsWith('/search');
    } catch (_) {
      return false;
    }
  }

  function refineSearchForm(form) {
    if (!(form instanceof HTMLFormElement) || !isSearchForm(form)) return;

    const visible = form.querySelector('input[type="search"]');
    const namedQ = form.querySelector('input[name="q"]');
    const raw = String(visible?.value || namedQ?.value || '').trim();
    const original = looksStructuredQuery(raw)
      ? (originalQueryFromUrl() || storedOriginalQuery())
      : displayQuery(raw);
    if (!original) return;

    storeOriginalQuery(original);
    lastQuery = original;

    const qInput = form.querySelector('input[name="q"]');
    const serverQuery = strictStorefrontQuery(original);
    if (qInput) qInput.value = serverQuery;
    if (visible instanceof HTMLInputElement) visible.value = original;

    removeHiddenInputs(form, ORIGINAL_PARAM);
    if (serverQuery !== original) upsertHiddenInput(form, ORIGINAL_PARAM, original);
    upsertHiddenInput(form, 'type', 'product');
    upsertHiddenInput(form, 'options[fields]', isPlatformDiscoveryQuery(original) ? DISCOVERY_FIELDS : PRECISE_FIELDS);
    upsertHiddenInput(form, 'options[prefix]', 'none');
  }

  function refineSearchLink(anchor) {
    if (!(anchor instanceof HTMLAnchorElement)) return;
    try {
      const url = new URL(anchor.href, window.location.origin);
      if (!url.pathname.endsWith('/search')) return;

      const raw = (url.searchParams.get('q') || '').trim();
      const original = (url.searchParams.get(ORIGINAL_PARAM) || '').trim()
        || (looksStructuredQuery(raw) ? (storedOriginalQuery().trim() || originalQueryFromUrl()) : displayQuery(raw));
      if (!original) return;

      storeOriginalQuery(original);
      lastQuery = original;
      const serverQuery = strictStorefrontQuery(original);
      url.searchParams.set('q', serverQuery);
      if (serverQuery !== original) url.searchParams.set(ORIGINAL_PARAM, original);
      else url.searchParams.delete(ORIGINAL_PARAM);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[fields]', isPlatformDiscoveryQuery(original) ? DISCOVERY_FIELDS : PRECISE_FIELDS);
      url.searchParams.set('options[prefix]', 'none');
      anchor.href = url.toString();
    } catch (_) {}
  }

  function cleanCurrentSearchUrl() {
    try {
      if (!window.location.pathname.endsWith('/search')) return;

      const params = new URLSearchParams(window.location.search);
      const raw = (params.get('q') || '').trim();
      const explicit = (params.get(ORIGINAL_PARAM) || '').trim();
      const original = explicit || (looksStructuredQuery(raw) ? storedOriginalQuery().trim() : displayQuery(raw));
      if (!original) return;

      storeOriginalQuery(original);
      lastQuery = original;
      let changed = false;

      // Keep a structured technical q in the URL: infinite scroll reuses it.
      // Only clean the legacy sgp_q parameter when q itself is not structured.
      if (!looksStructuredQuery(raw) && params.has(ORIGINAL_PARAM)) {
        params.delete(ORIGINAL_PARAM);
        changed = true;
      }

      if (changed) {
        const query = params.toString();
        const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
        window.history.replaceState(window.history.state, '', nextUrl);
      }
    } catch (_) {}
  }

  function cleanVisibleSearchInputs() {
    const original = originalQueryFromUrl() || storedOriginalQuery();
    if (!original) return;
    document.querySelectorAll('input[type="search"]').forEach(input => {
      if (input instanceof HTMLInputElement && looksStructuredQuery(input.value)) {
        input.value = original;
      }
    });
  }

  if (typeof window.fetch === 'function') {
    const nativeFetch = window.fetch.bind(window);
    window.fetch = function(input, init) {
      if (typeof input === 'string' || input instanceof URL) return nativeFetch(refinePredictiveUrl(input), init);
      return nativeFetch(input, init);
    };
  }

  if (window.XMLHttpRequest && XMLHttpRequest.prototype.open) {
    const nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      return nativeOpen.call(this, method, refinePredictiveUrl(url), ...rest);
    };
  }

  document.addEventListener('input', event => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.type === 'search') {
      lastQuery = displayQuery(input.value);
      if (lastQuery) storeOriginalQuery(lastQuery);
      scheduleFilter();
    }
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    if (form instanceof HTMLFormElement) refineSearchForm(form);
  }, true);

  document.addEventListener('click', event => {
    const anchor = event.target.closest?.('a[href]');
    if (anchor) refineSearchLink(anchor);
  }, true);

  const observer = new MutationObserver(mutations => {
    if (mutations.some(mutation => mutation.addedNodes.length || mutation.removedNodes.length)) {
      cleanPredictiveSearchLabels();
      scheduleFilter();
    }
  });

  function init() {
    cleanCurrentSearchUrl();
    cleanVisibleSearchInputs();
    lastQuery = getCurrentQuery();
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
    scheduleFilter();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  window.addEventListener('pageshow', () => {
    cleanCurrentSearchUrl();
    cleanVisibleSearchInputs();
    lastQuery = getCurrentQuery();
    scheduleFilter();
  });
})();
